import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Building2, LogOut, LayoutDashboard, Users, Settings, Lock, Loader2, Bot, ClipboardList, MessageSquare, CalendarDays, CalendarClock, Radio, Calendar, Mail, Bell, BellOff, Volume2, VolumeX, Briefcase, Mic, Zap, Gift, Phone, PhoneOutgoing, GitBranch
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import AdminDashboard from "./admin/AdminDashboard";
import AdminLeadsList from "./admin/AdminLeadsList";
import AdminLeadDetail from "./admin/AdminLeadDetail";
import AdminSettings from "./admin/AdminSettings";
import SystemPromptEditor from "./admin/SystemPromptEditor";
import AdminHypeConnectChats from "./admin/AdminHypeConnectChats";
import AdminHypeConnectPromos from "./admin/AdminHypeConnectPromos";
import AdminVoiceBot from "./admin/AdminVoiceBot";
import AdminVoiceCampaigns from "./admin/AdminVoiceCampaigns";
import QualificationRulesEditor from "./admin/QualificationRulesEditor";
import AdminChatSessions from "./admin/AdminChatSessions";
import AdminAppointments from "./admin/AdminAppointments";
import AdminAppointmentSlots from "./admin/AdminAppointmentSlots";
import AdminChatMode from "./admin/AdminChatMode";
import AdminAppointmentDays from "./admin/AdminAppointmentDays";
import AdminBulkEmail from "./admin/AdminBulkEmail";
import AdminAdvisors from "./admin/AdminAdvisors";
import AdminJobVariants from "./admin/AdminJobVariants";
import AdminVoiceSettings from "./admin/AdminVoiceSettings";
import AdminAutomation from "./admin/AdminAutomation";
import AdminPromptVersions from "./admin/AdminPromptVersions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification Sound (Base64 encoded short beep)
// Longer notification sound (ding-dong style)
const NOTIFICATION_SOUND_URL = "data:audio/wav;base64,UklGRl9PAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YTs/AACAgoSGiIqMjo+RkpOUlZWVlZSUk5KRj46MioeEgn98eXZzcG1qaGViX1xaV1VSUE1LSEdFQ0JAQD8/Pz9AQEFCREVHSUpNT1JUV1pcX2JlZ2psb3J1eHt+gYSHio2PkpSWl5mam5ycnJycnJuamZiWlJKPjYqHhIB9endzb2xoZWFdWlZTT0xIRUJAPjw6ODc1NDMyMTExMTEyMzQ1Nzg6PD5AQkVITFBTVlpdYGRnbHBzdnl9gISHi46RlZeZm5ydnp+fn5+fn56dnJqYlpSSj42KhoJ/fHh0cGxoZGBcWFRQTEhFQT48Ojg2NDMyMDAvLy8vLzAxMjM1Nzk7PUBDRklMUFNXWl1hZGhsb3N2en6BhYmMkJOWmJqcnZ6foKCgoKCfnp2cmpmXlZKQjYqGg398eHRwbGhkYFtXU09LR0RAPTs5NzUzMTAvLi4uLi4vMDEyNDY4Oj1AQ0ZJTVBUWFtfYmZqbnF1eX2Ag4eMkJOWmJudnp+goaGhoaGhoJ+enJuZl5WSkI2KhoN/fHh0cGxoZGBcWFRQS0dEQD07OTc1MzEvLi0tLS0tLi8wMjQ2ODo9QENGSk1RVVhcYGNnaW1xdHh8f4OGio6SlpmbnZ6foKChoaGhoaGgn56cmpmXlZKQjYqGgn98eHRwbGhkX1tXU09LR0RAPTs5NzQyMC8uLSwsLCwtLi8xMzU3Oj1AQkZJTVFUWFxfY2drcHR4fICEh4uPk5aZm52en6ChoaKioqKioaGgn56cmpmXlJKPjIiEgX15dXFtaWVhXVlVUUxIRUE+PDo4NjQyMS8uLSwsLCwtLi8xMzU4Oj1AQ0ZKTlFVWV1gZGhsc3d7f4OHi4+SlpmbnZ+goaKjo6Ojo6OioqGgn52bmJaUkY6LiIR/fHh0cG1pZWFdWVVRT0tIREE/PTk3NTMxLy4tLCsrKywsLS4wMjQ2OTs+QUNHSk5SV1peYmZqbnJ2en6Ch4uPk5eam56goaKjpKSkpKSkpKOioqCfnZuZlpSSj4yIhYF9eXZyb2xoZGBdWVVRTkpGQ0A+PDo4NjQyMC8uLS0sLCwsLS4vMTM1ODo8P0JFSEtPUlZaXmFlamxxdXl9gYWJjZCUl5qcn6Cho6SlpaampqampqWlpKOioJ6cmpmWlJKPjImFgn57d3Rxbmlm";

// Lead Notification Hook
const useLeadNotifications = (authToken, enabled) => {
  const lastLeadCountRef = useRef(null);
  const audioRef = useRef(null);
  
  const playNotificationSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
        audioRef.current.volume = 1.0; // Full volume
      }
      // Play sound multiple times for attention
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log("Audio play failed:", e));
    } catch (e) {
      console.log("Audio error:", e);
    }
  }, []);

  const showBrowserNotification = useCallback((leadName) => {
    if (Notification.permission === "granted") {
      const notification = new Notification("🔔 Neuer Lead!", {
        body: `${leadName || "Ein neuer Bewerber"} hat sich registriert!`,
        icon: "/favicon.ico",
        tag: "new-lead-" + Date.now(), // Unique tag for each notification
        requireInteraction: true,
        silent: false
      });
      
      // Auto-close after 30 seconds
      setTimeout(() => notification.close(), 30000);
      
      // Focus window on click
      notification.onclick = () => {
        window.focus();
        window.location.hash = "#leads";
        notification.close();
      };
    }
  }, []);

  // Exposed test function
  const testNotification = useCallback(() => {
    playNotificationSound();
    showBrowserNotification("Test-Bewerber");
    toast.success("🔔 TEST: Neuer Lead: Test-Bewerber!", {
      duration: 10000,
      action: {
        label: "Anzeigen",
        onClick: () => window.location.hash = "#leads"
      }
    });
  }, [playNotificationSound, showBrowserNotification]);

  const checkForNewLeads = useCallback(async () => {
    if (!authToken || !enabled) return;
    
    try {
      const response = await axios.get(`${API}/admin/leads?page=1&page_size=1`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      const currentCount = response.data.total;
      const latestLead = response.data.leads?.[0];
      
      // On first load, just store the count
      if (lastLeadCountRef.current === null) {
        lastLeadCountRef.current = currentCount;
        return;
      }
      
      // If count increased, we have a new lead!
      if (currentCount > lastLeadCountRef.current) {
        const leadName = latestLead?.first_name || "Neuer Bewerber";
        playNotificationSound();
        showBrowserNotification(leadName);
        toast.success(`🔔 Neuer Lead: ${leadName}!`, {
          duration: 10000,
          action: {
            label: "Anzeigen",
            onClick: () => window.location.hash = "#leads"
          }
        });
        lastLeadCountRef.current = currentCount;
      }
    } catch (error) {
      // Silently fail - don't spam errors
    }
  }, [authToken, enabled, playNotificationSound, showBrowserNotification]);

  useEffect(() => {
    if (!enabled) return;
    
    // Request notification permission
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }
    
    // Check immediately
    checkForNewLeads();
    
    // Then check every 10 seconds
    const interval = setInterval(checkForNewLeads, 10000);
    
    return () => clearInterval(interval);
  }, [enabled, checkForNewLeads]);

  return { checkForNewLeads, testNotification };
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem("admin_token", response.data.token);
      localStorage.setItem("admin_username", response.data.username);
      localStorage.setItem("admin_role", response.data.role);
      onLogin(response.data.token, response.data.username, response.data.role);
      toast.success("Erfolgreich angemeldet");
    } catch (err) {
      setError("Ungültige Anmeldedaten");
      toast.error("Ungültige Anmeldedaten");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="bg-white border-slate-200 shadow-xl" data-testid="login-card">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-blue-500/30">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Admin Login
              </h1>
              <p className="text-slate-500 mt-2">Hype-Factory Backoffice</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-sm font-medium text-slate-700">
                  Benutzername
                </Label>
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="admin"
                  className="rounded-lg"
                  data-testid="admin-username-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Passwort
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Passwort eingeben"
                  className={`rounded-lg ${error ? 'border-red-500' : ''}`}
                  data-testid="admin-password-input"
                  autoFocus
                />
                {error && <p className="text-xs text-red-500">{error}</p>}
              </div>

              <Button
                type="submit"
                disabled={isLoading || !password}
                className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-full py-6 text-base font-semibold shadow-lg shadow-blue-500/30"
                data-testid="admin-login-button"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Anmelden...
                  </>
                ) : (
                  "Anmelden"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

// Navigation Items
// `hypeconnectOnly: true` → shown ONLY for hypeconnect project
// `hideOnProjects` → hidden for listed projects (used to hide normal admin tabs for hypeconnect)
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, hideOnProjects: ['hypeconnect'] },
  { id: 'leads', label: 'Leads', icon: Users, hideOnProjects: ['hypeconnect'] },
  { id: 'advisors', label: 'Berater', icon: Users, hideOnProjects: ['hypeconnect'] },
  { id: 'appointments', label: 'Termine', icon: CalendarDays, hideOnProjects: ['hypeconnect'] },
  { id: 'slots', label: 'Termin-Slots', icon: CalendarClock, hideOnProjects: ['hypeconnect'] },
  { id: 'appointmentdays', label: 'Termin-Tage', icon: Calendar, hideOnProjects: ['hypeconnect'] },
  { id: 'chats', label: 'Alle Chats', icon: MessageSquare, hideOnProjects: ['hypeconnect'] },
  { id: 'bulkemail', label: 'Massen-E-Mail', icon: Mail, hideOnProjects: ['hypeconnect'] },
  { id: 'chatmode', label: 'Chat-Modus', icon: Radio, hideOnProjects: ['hypeconnect'] },
  { id: 'jobvariants', label: 'Job-Varianten', icon: Briefcase, projectsOnly: ['factoryhype', 'factoryhype_v2'] },
  { id: 'voicesettings', label: 'Voice-Aufnahme', icon: Mic, hideOnProjects: ['hypeconnect'] },
  { id: 'automation', label: 'Automatisierung', icon: Zap, projectsOnly: ['factoryhype', 'factoryhype_v2'] },
  { id: 'rules', label: 'Qualifizierung', icon: ClipboardList, hideOnProjects: ['hypeconnect'] },
  { id: 'prompt', label: 'KI-Prompt', icon: Bot },
  { id: 'promptversions', label: 'Prompt A/B-Toggle', icon: GitBranch, projectsOnly: ['hypefactory'] },
  { id: 'hypeconnect-chats', label: 'Chat-Verläufe', icon: MessageSquare, projectsOnly: ['hypeconnect'] },
  { id: 'hypeconnect-promos', label: 'Aktionen', icon: Gift, projectsOnly: ['hypeconnect'] },
  { id: 'voice-bot', label: 'Voice-Bot', icon: Phone, projectsOnly: ['hypefactory'] },
  { id: 'voice-campaigns', label: 'Voice-Kampagnen', icon: PhoneOutgoing, projectsOnly: ['hypefactory'] },
  { id: 'settings', label: 'Einstellungen', icon: Settings, hideOnProjects: ['hypeconnect'] },
];

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [username, setUsername] = useState("");
  const [role, setRole] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [viewParams, setViewParams] = useState({});
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  
  // Multi-project support
  const [currentProject, setCurrentProject] = useState(() => {
    return localStorage.getItem("admin_current_project") || "hypefactory";
  });
  const [projects, setProjects] = useState([
    { id: "hypefactory", name: "HypeFactory", primary_color: "#3B82F6" },
    { id: "factoryhype", name: "FactoryHype", primary_color: "#10B981" },
    { id: "factoryhype_v2", name: "FactoryHype V2", primary_color: "#EC4899" },
    { id: "hypeconnect", name: "HypeConnect", primary_color: "#6366F1" }
  ]);
  
  // Lead Notification State
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem("lead_notifications_enabled") === "true";
  });

  // Use the notification hook
  const { testNotification } = useLeadNotifications(authToken, notificationsEnabled);
  
  // Handle project change
  const handleProjectChange = (projectId) => {
    setCurrentProject(projectId);
    localStorage.setItem("admin_current_project", projectId);
    // HypeConnect only exposes the KI-Prompt view → auto-jump there
    if (projectId === "hypeconnect") {
      setCurrentView("prompt");
      setSelectedLeadId(null);
    }
  };

  const toggleNotifications = () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    localStorage.setItem("lead_notifications_enabled", newValue.toString());
    
    if (newValue) {
      // Request permission when enabling
      if (Notification.permission === "default") {
        Notification.requestPermission().then(permission => {
          if (permission === "granted") {
            toast.success("🔔 Lead-Benachrichtigungen aktiviert!");
          } else {
            toast.error("Browser-Benachrichtigungen wurden blockiert");
          }
        });
      } else if (Notification.permission === "granted") {
        toast.success("🔔 Lead-Benachrichtigungen aktiviert!");
      } else {
        toast.error("Browser-Benachrichtigungen wurden blockiert. Bitte in Browser-Einstellungen erlauben.");
      }
    } else {
      toast.info("🔕 Lead-Benachrichtigungen deaktiviert");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    const storedUsername = localStorage.getItem("admin_username");
    const storedRole = localStorage.getItem("admin_role");
    if (token) {
      verifyToken(token, storedUsername, storedRole);
    } else {
      setIsLoading(false);
    }
  }, []);

  // For HypeConnect: only allow the 'prompt' and 'hypeconnect-chats' views (other nav items are hidden)
  useEffect(() => {
    if (currentProject === "hypeconnect" && currentView !== "prompt" && currentView !== "hypeconnect-chats" && currentView !== "hypeconnect-promos") {
      setCurrentView("prompt");
      setSelectedLeadId(null);
    }
  }, [currentProject, currentView]);

  const verifyToken = async (token, storedUsername, storedRole) => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuthToken(token);
      setUsername(storedUsername || "admin");
      setRole(storedRole || "admin");
      setIsAuthenticated(true);
    } catch (err) {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_username");
      localStorage.removeItem("admin_role");
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = (token, username, role) => {
    setAuthToken(token);
    setUsername(username);
    setRole(role);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    localStorage.removeItem("admin_role");
    setAuthToken(null);
    setIsAuthenticated(false);
    toast.success("Erfolgreich abgemeldet");
  };

  const handleNavigate = (view, params = {}) => {
    setCurrentView(view);
    setViewParams(params);
    setSelectedLeadId(null);
  };

  const handleViewLead = (leadId) => {
    setSelectedLeadId(leadId);
  };

  const handleBackFromLead = () => {
    setSelectedLeadId(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return (
    <div className="h-screen bg-slate-50 flex overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col fixed h-full">
        {/* Logo with Project Switcher */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center gap-3 mb-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
              style={{ 
                background: currentProject === "hypeconnect"
                  ? "linear-gradient(to bottom right, #6366F1, #8B5CF6)"
                  : currentProject === "factoryhype_v2"
                  ? "linear-gradient(to bottom right, #EC4899, #F97316)"
                  : currentProject === "factoryhype" 
                  ? "linear-gradient(to bottom right, #10B981, #14B8A6)" 
                  : "linear-gradient(to bottom right, #3B82F6, #8B5CF6)",
                boxShadow: currentProject === "hypeconnect"
                  ? "0 10px 15px -3px rgba(99, 102, 241, 0.2)"
                  : currentProject === "factoryhype_v2"
                  ? "0 10px 15px -3px rgba(236, 72, 153, 0.2)"
                  : currentProject === "factoryhype"
                  ? "0 10px 15px -3px rgba(16, 185, 129, 0.2)"
                  : "0 10px 15px -3px rgba(59, 130, 246, 0.2)"
              }}
            >
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {projects.find(p => p.id === currentProject)?.name || "HypeFactory"}
              </h1>
              <p className="text-xs text-slate-500">Backoffice</p>
            </div>
          </div>
          
          {/* Project Switcher */}
          <div className="flex gap-1 p-1 bg-slate-100 rounded-lg" data-testid="project-switcher">
            {projects.map(project => (
              <button
                key={project.id}
                onClick={() => handleProjectChange(project.id)}
                className={`flex-1 text-xs font-medium py-1.5 px-2 rounded-md transition-all ${
                  currentProject === project.id
                    ? 'bg-white shadow-sm text-slate-900'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                style={currentProject === project.id ? {
                  borderBottom: `2px solid ${project.primary_color}`
                } : {}}
                data-testid={`project-${project.id}`}
              >
                {project.name}
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-1">
            {NAV_ITEMS
              .filter(item => {
                if (item.projectsOnly && !item.projectsOnly.includes(currentProject)) return false;
                if (item.hideOnProjects && item.hideOnProjects.includes(currentProject)) return false;
                return true;
              })
              .map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id && !selectedLeadId;
              return (
                <li key={item.id}>
                  <button
                    onClick={() => handleNavigate(item.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid={`nav-${item.id}`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-slate-200">
          {/* Notification Toggle */}
          <div className="mb-3 p-2 rounded-lg bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {notificationsEnabled ? (
                  <Bell className="w-4 h-4 text-green-600" />
                ) : (
                  <BellOff className="w-4 h-4 text-slate-400" />
                )}
                <span className="text-xs font-medium text-slate-600">Lead-Alarm</span>
              </div>
              <Button
                variant={notificationsEnabled ? "default" : "outline"}
                size="sm"
                onClick={toggleNotifications}
                className={`h-7 px-2 text-xs ${
                  notificationsEnabled 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'text-slate-500'
                }`}
                data-testid="notification-toggle"
              >
                {notificationsEnabled ? (
                  <>
                    <Volume2 className="w-3 h-3 mr-1" />
                    AN
                  </>
                ) : (
                  <>
                    <VolumeX className="w-3 h-3 mr-1" />
                    AUS
                  </>
                )}
              </Button>
            </div>
            {/* Test Button */}
            {notificationsEnabled && (
              <Button
                variant="outline"
                size="sm"
                onClick={testNotification}
                className="w-full h-7 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                data-testid="notification-test"
              >
                🔔 Test Alarm
              </Button>
            )}
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                <span className="text-sm font-medium text-slate-600">
                  {username.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-slate-900">{username}</p>
                <p className="text-xs text-slate-500 capitalize">{role}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              className="rounded-lg text-slate-400 hover:text-red-600"
              onClick={handleLogout}
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-6 overflow-y-auto h-screen">
        <div className="max-w-7xl mx-auto">
          {selectedLeadId ? (
            <AdminLeadDetail 
              leadId={selectedLeadId}
              authToken={authToken}
              onBack={handleBackFromLead}
              onViewLead={handleViewLead}
            />
          ) : currentView === 'dashboard' ? (
            <AdminDashboard 
              authToken={authToken}
              onNavigate={handleNavigate}
              projectId={currentProject}
            />
          ) : currentView === 'leads' ? (
            <AdminLeadsList 
              authToken={authToken}
              initialFilters={viewParams}
              onViewLead={handleViewLead}
              projectId={currentProject}
            />
          ) : currentView === 'appointments' ? (
            <AdminAppointments 
              authToken={authToken}
            />
          ) : currentView === 'slots' ? (
            <AdminAppointmentSlots 
              authToken={authToken}
            />
          ) : currentView === 'appointmentdays' ? (
            <AdminAppointmentDays />
          ) : currentView === 'chats' ? (
            <AdminChatSessions 
              authToken={authToken}
              userRole={role}
              projectId={currentProject}
            />
          ) : currentView === 'chatmode' ? (
            <AdminChatMode />
          ) : currentView === 'jobvariants' ? (
            <AdminJobVariants projectId={currentProject} />
          ) : currentView === 'bulkemail' ? (
            <AdminBulkEmail />
          ) : currentView === 'advisors' ? (
            <AdminAdvisors authToken={authToken} projectId={currentProject} />
          ) : currentView === 'rules' ? (
            <QualificationRulesEditor 
              authToken={authToken}
              userRole={role}
            />
          ) : currentView === 'prompt' ? (
            <SystemPromptEditor 
              authToken={authToken}
              userRole={role}
              projectId={currentProject}
            />
          ) : currentView === 'hypeconnect-chats' ? (
            <AdminHypeConnectChats authToken={authToken} />
          ) : currentView === 'hypeconnect-promos' ? (
            <AdminHypeConnectPromos authToken={authToken} />
          ) : currentView === 'voice-bot' ? (
            <AdminVoiceBot authToken={authToken} />
          ) : currentView === 'voice-campaigns' ? (
            <AdminVoiceCampaigns authToken={authToken} />
          ) : currentView === 'voicesettings' ? (
            <AdminVoiceSettings 
              authToken={authToken}
              projectId={currentProject}
            />
          ) : currentView === 'automation' ? (
            <AdminAutomation
              authToken={authToken}
              projectId={currentProject}
            />
          ) : currentView === 'promptversions' ? (
            <AdminPromptVersions authToken={authToken} />
          ) : currentView === 'settings' ? (
            <AdminSettings 
              authToken={authToken}
              userRole={role}
            />
          ) : null}
        </div>
      </main>
    </div>
  );
}
